import React from 'react';
import {graphql} from 'react-relay';
import {Redirect} from 'react-router-dom';
import Styled from 'styled-components/macro';
import {ForecastQueryRenderer} from '../../ForecastQueryRenderer';
import CompanyViewHeader from './CompanyViewHeader';
import ContactsListPanel from './ContactsListPanel';
import CompanyViewOverview from './CompanyViewOverview';
import {hasFeatureFlag} from '../shared/util/FeatureUtil';
import {hasModule} from '../shared/util/ModuleUtil';
import {canReachClientsList, hasPermission} from '../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import {FEATURE_FLAGS, MODULE_TYPES} from '../../constants';

// The 1920px layout fills the content column fluidly; on ultra-wide screens it would sprawl
// edge-to-edge, so cap it at >=2137px (IGNITE-479 AC) — same breakpoint as my-timesheets-page.
//
// Capped but NOT centred (IGNITE-650): the breakpoint measures the viewport, but a 256px
// ClientSecondaryNavigation sits beside this page, so `margin: 0 auto` centres inside a narrower
// column and leaves ~107px of dead space against the nav at 2560px.
const PageContent = Styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	width: 100%;
	@media screen and (min-width: 2137px) {
		max-width: 2089px;
		margin-right: auto;
	}
`;

type ClientStatus = {
	id: string;
	standing: 'ACTIVE' | 'INACTIVE';
};

type PrimaryContact = {
	id: string;
	firstName: string | null;
	lastName: string | null;
};

type ProjectNode = {
	id: string;
	name: string | null;
	companyProjectId: number | null;
	fullAccessToProject: boolean | null;
	projectEndYear: number | null;
	projectEndMonth: number | null;
	projectEndDay: number | null;
};

export type ClientContactNode = {
	id: string;
	firstName: string | null;
	lastName: string | null;
	email: string | null;
	phone: string | null;
	jobTitle: string | null;
	status: string | null;
	isGuestUser: boolean | null;
	createdAt: string | null;
	updatedAt: string | null;
	person: {id: string} | null;
	projects: {
		edges: Array<{node: ProjectNode | null} | null> | null;
	} | null;
};

export type PersonProjectAccess = {
	personProjectId: string;
	projectId: string;
	companyProjectId: number | null;
	projectName: string;
	projectEndYear: number | null;
	projectEndMonth: number | null;
	projectEndDay: number | null;
};

export type ViewerProjectNode = {
	id: string;
	name: string | null;
	companyProjectId: number | null;
	projectEndYear: number | null;
	projectEndMonth: number | null;
	projectEndDay: number | null;
	client: {id: string} | null;
	projectPersons: {
		edges: Array<{node: {id: string; person: {id: string} | null} | null} | null> | null;
	} | null;
};

type Client = {
	id: string;
	name: string;
	website: string | null;
	city: string | null;
	country: string | null;
	region: string | null;
	statusId: string | null;
	createdAt: string | null;
	updatedAt: string | null;
	notes: string | null;
	notesUpdatedAt: string | null;
};

type InnerProps = {
	client: Client | null;
	status: ClientStatus | null;
	primaryContact: PrimaryContact | null;
	clientContacts: ClientContactNode[];
	viewerProjects: ViewerProjectNode[];
	companyId: string;
	hasInvoices: boolean;
	// Re-runs companyViewPageQuery (store-and-network) so the contacts list reflects a create/delete
	// without a manual reload. Threaded from the ForecastQueryRenderer render-prop `retry`.
	refetchContacts?: () => void;
	// Ids of this client's contacts referenced by an invoice — their Delete action is disabled.
	invoicedContactIds?: Set<string>;
};

export const CompanyViewPageInner: React.FC<InnerProps> = ({
	client,
	status,
	primaryContact,
	clientContacts,
	viewerProjects,
	companyId,
	hasInvoices,
	refetchContacts,
	invoicedContactIds,
}) => {
	if (!client) {
		// Invalid / non-existent client ID behind the feature flag → 404 (IGNITE-479 AC).
		return <Redirect to="/not-found" />;
	}

	const isContactsModuleActive =
		hasFeatureFlag(FEATURE_FLAGS.NEW_CLIENT_CONTACTS_FEATURE) && hasModule(MODULE_TYPES.NEW_CLIENT_CONTACTS_FEATURE);

	// The edit gate for the whole page: the same permission that the client and client-contact
	// update/create/delete resolvers validate, so the UI and the resolver cannot disagree. Gating
	// on the Collaborator *profile* instead would lock out an admin who granted that profile the
	// permission, and would miss Contributor and every custom profile that lacks it — all of
	// which hit the same unauthorized response, which the GraphQL error formatter has no branch
	// for and surfaces as an opaque "Internal Error: <correlationId>".
	const canEditClient = hasPermission(PERMISSION_TYPE.CLIENT_UPDATE);

	// A SECOND, independent gate, for the "All Clients" breadcrumb and the header's admin-only
	// Integration Links item. See canReachClientsList for why this is not derived from
	// canEditClient.
	const canReachClients = canReachClientsList();

	// A THIRD gate, for the ✕ that removes a contact from a project. Computed here rather than
	// inside ContactsListPanel so all three gates on this page are derived in one place and the
	// panel stays drivable from props alone. Unlike the other two, this one is NOT enforced by the
	// backend — see the note at its use site in ContactsListPanel.
	const canRemoveProjectPerson = hasPermission(PERMISSION_TYPE.PROJECT_PERSON_DELETE);

	return (
		<PageContent>
			<CompanyViewHeader
				client={client}
				status={status}
				primaryContact={primaryContact}
				companyId={companyId}
				hasInvoices={hasInvoices}
				hasContacts={clientContacts.length > 0}
				canEditClient={canEditClient}
				canReachClients={canReachClients}
			/>
			<CompanyViewOverview
				clientId={client.id}
				notes={client.notes}
				notesUpdatedAt={client.notesUpdatedAt}
				canEditClient={canEditClient}
				mainContent={
					isContactsModuleActive ? (
						<ContactsListPanel
							clientContacts={clientContacts}
							primaryContactId={primaryContact?.id ?? null}
							viewerProjects={viewerProjects}
							clientId={client.id}
							clientName={client.name ?? ''}
							onContactsChanged={refetchContacts}
							invoicedContactIds={invoicedContactIds}
							canEditClient={canEditClient}
							canRemoveProjectPerson={canRemoveProjectPerson}
						/>
					) : undefined
				}
			/>
		</PageContent>
	);
};

const companyViewPageQuery = graphql`
	query CompanyViewPage_Query($clientId: String, $contactFilters: [GlobalSearchFilterType]) {
		viewer {
			actualPersonId
			invoicesV2(first: 100000) {
				edges {
					node {
						id
						client {
							id
						}
						clientContact {
							id
						}
					}
				}
			}
			clientContacts(first: 1000, searchQuery: {filters: $contactFilters}) {
				edges {
					node {
						id
						firstName
						lastName
						email
						phone
						jobTitle
						status
						isGuestUser
						createdAt
						updatedAt
						person {
							id
						}
						projects(first: 100) {
							edges {
								node {
									id
									name
									companyProjectId
									fullAccessToProject
									projectEndYear
									projectEndMonth
									projectEndDay
								}
							}
						}
					}
				}
			}
			company {
				id
				client(id: $clientId) {
					id
					name
					website
					city
					country
					region
					statusId
					createdAt
					updatedAt
					notes
					notesUpdatedAt
					status {
						id
						standing
					}
					primaryContact {
						id
						firstName
						lastName
					}
				}
			}
			projects(first: 100000) @connection(key: "Viewer_projects", filters: []) {
				edges {
					node {
						id
						name
						companyProjectId
						projectEndYear
						projectEndMonth
						projectEndDay
						client {
							id
						}
						projectPersons(first: 1000, onlyClientUsers: true)
							@connection(key: "Project_projectPersons", filters: []) {
							edges {
								node {
									id
									person {
										id
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

type CompanyViewPageProps = {
	clientId: string;
};

const CompanyViewPage: React.FC<CompanyViewPageProps> = ({clientId}) => {
	return (
		<ForecastQueryRenderer
			key={'query-render-companyViewPage-' + clientId}
			query={companyViewPageQuery}
			variables={{clientId, contactFilters: [{field: 'CLIENT_CONTACT_CLIENT_ID', value: [clientId]}]}}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			render={(relayProps: any, retry: () => void) => {
				const company = relayProps?.viewer?.company;
				const client = company?.client ?? null;
				const status = client?.status ?? null;
				const primaryContact = client?.primaryContact ?? null;
				const clientContacts: ClientContactNode[] =
					relayProps?.viewer?.clientContacts?.edges?.flatMap((e: {node: ClientContactNode | null} | null) =>
						e?.node ? [e.node] : []
					) ?? [];
				const companyId = company?.id ?? '';
				// A client cannot be deleted once it has any invoice — same rule the old clients page uses.
				const hasInvoices: boolean = (relayProps?.viewer?.invoicesV2?.edges ?? []).some(
					(e: {node: {client: {id: string} | null} | null} | null) => e?.node?.client?.id === client?.id
				);
				// Invoiced-contact delete protection (Area C) is gated on the extending flag + module —
				// matching the Invoice.clientContact resolver. When off, the set is empty (no gating).
				const invoicedContactsEnabled =
					hasFeatureFlag(FEATURE_FLAGS.EXTENDING_CLIENT_CONTACTS) &&
					hasModule(MODULE_TYPES.NEW_CLIENT_CONTACTS_FEATURE);
				const invoicedContactIds: Set<string> = new Set<string>(
					invoicedContactsEnabled
						? (relayProps?.viewer?.invoicesV2?.edges ?? []).flatMap(
								(
									e: {node: {client: {id: string} | null; clientContact: {id: string} | null} | null} | null
								) => {
									const contactId = e?.node?.clientContact?.id;
									return e?.node?.client?.id === client?.id && contactId ? [contactId] : [];
								}
						  )
						: []
				);
				const viewerProjects: ViewerProjectNode[] =
					relayProps?.viewer?.projects?.edges?.flatMap((e: {node: ViewerProjectNode | null} | null) =>
						e?.node ? [e.node] : []
					) ?? [];
				return (
					<CompanyViewPageInner
						client={client}
						status={status}
						primaryContact={primaryContact}
						clientContacts={clientContacts}
						viewerProjects={viewerProjects}
						companyId={companyId}
						hasInvoices={hasInvoices}
						refetchContacts={retry}
						invoicedContactIds={invoicedContactIds}
					/>
				);
			}}
		/>
	);
};

export default CompanyViewPage;
